// Media configuration and utilities for SitRepp.io
export interface MediaConfig {
  hero: {
    video: {
      src: string;
      poster: string;
      background: string;
    };
    image: {
      src: string;
      background: string;
    };
  };
  hero2: {
    video: {
      src: string;
      poster: string;
      background: string;
    };
    image: {
      src: string;
      background: string;
    };
  };
  gallery: {
    images: string[];
  };
  mockups: {
    dashboard: string;
    tripDetail: string;
    gearManagement: string;
    mobile: string;
    photoSharing: string;
  };
  problem: {
    groupChatGif: string; // GIF showing chaotic group chat
  };
}

// When set, rewrite Supabase public-storage URLs to the CloudFront domain
// (private S3 origin). Unset = keep serving from Supabase (pre-cutover).
const CLOUDFRONT_DOMAIN = import.meta.env.VITE_CLOUDFRONT_DOMAIN as string | undefined;

/** Domains whose URLs point at our managed object storage (Supabase or CloudFront). */
function isManagedAssetUrl(url: string): boolean {
  return url.includes('supabase.co') || (!!CLOUDFRONT_DOMAIN && url.includes(CLOUDFRONT_DOMAIN));
}

/**
 * Rewrite a Supabase public-storage URL to CloudFront when configured.
 * `https://<proj>.supabase.co/storage/v1/object/public/<bucket>/<key>`
 *   → `https://<cloudfront>/<bucket>/<key>`
 */
function cdn(url: string): string {
  if (!CLOUDFRONT_DOMAIN) return url;
  return url.replace(
    /https:\/\/[^/]+\.supabase\.co\/storage\/v1\/object\/public\//,
    `https://${CLOUDFRONT_DOMAIN}/`
  );
}

/**
 * Convert image URLs to the Lambda-optimized .webp version and route through
 * the active CDN. Non-image assets (mp4, mov, gif) should use `cdn()` directly.
 */
function toWebpUrl(url: string): string {
  const webp = isManagedAssetUrl(url) && /\.(jpg|jpeg|png|heic|heif)$/i.test(url)
    ? url.replace(/\.(jpg|jpeg|png|heic|heif)$/i, '.webp')
    : url;
  return cdn(webp);
}

// Production media URLs - Using your actual Supabase Storage URLs
// NOTE: Image URLs are automatically converted to .webp versions created by Lambda optimization
// NOTE: These large media files contribute to Supabase egress usage.
// Consider moving to Vercel Blob Storage, Cloudflare CDN, or another CDN
// to reduce Supabase egress costs, especially for landing page assets.
export const MEDIA_CONFIG: MediaConfig = {
  hero: {
    video: {
      src: cdn('https://nlwgjyzzpbkmccttmkow.supabase.co/storage/v1/object/public/landing-page/sitrepp%20cover.mp4'),
      poster: toWebpUrl('https://nlwgjyzzpbkmccttmkow.supabase.co/storage/v1/object/public/landing-page/DJI_20240831171640_0153_D.jpg'),
      background: toWebpUrl('https://nlwgjyzzpbkmccttmkow.supabase.co/storage/v1/object/public/landing-page/DJI_20240831171640_0153_D.jpg')
    },
    image: {
      src: toWebpUrl('https://nlwgjyzzpbkmccttmkow.supabase.co/storage/v1/object/public/landing-page/DJI_20240831171640_0153_D.jpg'),
      background: toWebpUrl('https://nlwgjyzzpbkmccttmkow.supabase.co/storage/v1/object/public/landing-page/DJI_20240831171640_0153_D.jpg')
    }
  },
  hero2: {
    video: {
      src: cdn('https://nlwgjyzzpbkmccttmkow.supabase.co/storage/v1/object/public/landing-page/sitrepp%20roll.mov'),
      poster: toWebpUrl('https://nlwgjyzzpbkmccttmkow.supabase.co/storage/v1/object/public/landing-page/DJI_20240831171640_0153_D.jpg'),
      background: toWebpUrl('https://nlwgjyzzpbkmccttmkow.supabase.co/storage/v1/object/public/landing-page/DJI_20240831171640_0153_D.jpg')
    },
    image: {
      src: toWebpUrl('https://nlwgjyzzpbkmccttmkow.supabase.co/storage/v1/object/public/landing-page/DJI_20240831171640_0153_D.jpg'),
      background: toWebpUrl('https://nlwgjyzzpbkmccttmkow.supabase.co/storage/v1/object/public/landing-page/DJI_20240831171640_0153_D.jpg')
    }
  },
  gallery: {
    images: [
      cdn('https://nlwgjyzzpbkmccttmkow.supabase.co/storage/v1/object/public/landing-page/DJI_20250826172705_0193_D.webp'),
      cdn('https://nlwgjyzzpbkmccttmkow.supabase.co/storage/v1/object/public/landing-page/DJI_20240831171640_0153_D.webp'),
      cdn('https://nlwgjyzzpbkmccttmkow.supabase.co/storage/v1/object/public/landing-page/campingstill3_1.3.1.webp')
    ]
  },
  mockups: {
    dashboard: toWebpUrl('https://nlwgjyzzpbkmccttmkow.supabase.co/storage/v1/object/public/landing-page/mockup-trip-dashboard.png'),
    tripDetail: toWebpUrl('https://nlwgjyzzpbkmccttmkow.supabase.co/storage/v1/object/public/landing-page/mockup-trip-details.png'),
    gearManagement: toWebpUrl('https://nlwgjyzzpbkmccttmkow.supabase.co/storage/v1/object/public/landing-page/mockup-gear.png'),
    mobile: toWebpUrl('https://nlwgjyzzpbkmccttmkow.supabase.co/storage/v1/object/public/landing-page/mock-meal.png'),
    photoSharing: toWebpUrl('https://nlwgjyzzpbkmccttmkow.supabase.co/storage/v1/object/public/landing-page/campingstill3_1.3.1.jpg')
  },
  problem: {
    groupChatGif: cdn('https://nlwgjyzzpbkmccttmkow.supabase.co/storage/v1/object/public/landing-page/Phone-notification-iOS-remix-ezgif.com-loop-count-loop.gif')
    // TODO: Upload your GIF to Supabase Storage bucket 'landing-page' and update this URL
    // Note: GIFs are not converted to WebP, so this URL remains unchanged
  }
};

// Utility function to get optimized image URL
// For Supabase Storage, this automatically uses .webp versions created by Lambda optimization
export function getOptimizedImageUrl(
  baseUrl: string, 
  width: number, 
  height: number, 
  quality: number = 80
): string {
  // For our managed storage (Supabase or CloudFront), replace image extensions
  // with .webp to use the Lambda-optimized versions.
  if (isManagedAssetUrl(baseUrl)) {
    try {
      const url = new URL(baseUrl);
      const pathname = url.pathname;
      
      // Replace image extensions with .webp (handles jpg, jpeg, png, heic, heif)
      // This uses the optimized .webp files created by AWS Lambda
      const webpPathname = pathname.replace(/\.(jpg|jpeg|png|heic|heif)$/i, '.webp');
      url.pathname = webpPathname;
      
      // Note: Supabase Storage doesn't support on-the-fly resizing via query params,
      // but we keep these for potential future CDN integration or documentation
      // The Lambda-optimized .webp files are already resized to max 1920x1920
      url.searchParams.set('width', width.toString());
      url.searchParams.set('height', height.toString());
      url.searchParams.set('resize', 'cover');
      url.searchParams.set('quality', quality.toString());
      
      return url.toString();
    } catch (error) {
      // If URL parsing fails, return original URL
      return baseUrl;
    }
  }
  
  // For Unsplash, add optimization parameters
  if (baseUrl.includes('unsplash.com')) {
    const url = new URL(baseUrl);
    url.searchParams.set('w', width.toString());
    url.searchParams.set('h', height.toString());
    url.searchParams.set('fit', 'crop');
    url.searchParams.set('crop', 'center');
    url.searchParams.set('auto', 'format');
    url.searchParams.set('q', quality.toString());
    return url.toString();
  }
  
  // For other providers, you might need different optimization
  return baseUrl;
}

// Get responsive background image URL
export function getResponsiveBackgroundImage(baseUrl: string): string {
  // Get screen dimensions
  const width = typeof window !== 'undefined' ? window.innerWidth : 1920;
  const height = typeof window !== 'undefined' ? window.innerHeight : 1080;
  
  // Use appropriate dimensions based on screen size
  const imageWidth = Math.min(width * 2, 3840); // Max 4K width, 2x for retina
  const imageHeight = Math.min(height * 2, 2160); // Max 4K height, 2x for retina
  
  return getOptimizedImageUrl(baseUrl, imageWidth, imageHeight, 85);
}

// Utility function to get video poster
export function getVideoPoster(videoUrl: string): string {
  // If using Cloudinary, you can generate a poster frame
  if (videoUrl.includes('cloudinary.com')) {
    return videoUrl.replace('/upload/', '/upload/so_0/').replace('.mp4', '.jpg');
  }
  
  // For other providers, you might need a separate poster image
  return videoUrl.replace('.mp4', '_poster.jpg');
}
